// Side-effect import: <std-accordion> must be registered before the group
// below hydrates one, and the class is otherwise referenced only as a type.
import "@tento-ui/components/accordion";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Accordion } from "@tento-ui/components/accordion";
import { Typography } from "@tento-ui/ui/typography";
import { TurnDiff } from "../thread/diff";

/** `tool` — one invocation, read as a sentence, with its output behind a
 * disclosure.
 *
 * This is the densest primitive in the family: tool activity is 44% of
 * every record we sampled. Harness surfaces re-emit the same call id as
 * title-less status updates, so the CALLER coalesces those into one view
 * and this component only ever renders the folded result — the shape all
 * three vendor protocols converge on (call and output are one item, not
 * two rows).
 *
 * ## Why the row lost its chip
 *
 * The first revision put the tool name in a bordered chip, then a verb,
 * then the subject, then a fact pinned to the right margin: four typographic
 * registers on a 20px line. Two things went wrong, and both are about a
 * COLUMN of rows rather than any single one.
 *
 * The chip's hairline was the heaviest ink on the row — heavier than the
 * status mark it sat next to — so a column of calls read as a wall of
 * boxes before any of the content came through. And because the chip sized
 * itself to its text, the subject started at a different x on every row,
 * so there was no edge to run the eye down either.
 *
 * The trailing fact was pinned right with margin-left:auto, which is the
 * same defect mirrored: "exit 0" and "6 sources" landed in a column of
 * their own, arbitrarily far from the subject they describe.
 *
 * What both vendor surfaces do instead is write a sentence — a mark, one
 * word for what happened, then what it happened to. So:
 *
 *     mark   label            subject                      note
 *      ●     Ran     cargo build -p tento-spool-server  ·  exit 0
 *
 * One label, not a chip AND a verb: `verb` when the dialect supplied one,
 * the tool's own name otherwise. A translator that wants to say both puts
 * the second in the subject, where it reads as language instead of as a
 * second widget.
 *
 * ## The nine call types, and the body they wanted
 *
 * OpenAI's Responses API does NOT have one call item — it has nine
 * (function, local_shell, web_search, file_search, code_interpreter,
 * image_generation, computer, mcp, custom), and ChatGPT renders them
 * visibly differently. Reading that as nine primitives is the wrong
 * lesson: every one of them is still mark + subject + status, and what
 * varies is the WORDING and one trailing fact.
 *
 * What they genuinely needed, and what the previous revision did not have,
 * is a per-kind BODY — a search's sources, an interpreter's code, a shell
 * call's output. That is `detail`, and it is CLOSED by default, because a
 * transcript is skimmed far more often than it is audited. This is the
 * same disclosure grammar `turn.thought` uses, deliberately: a reader
 * learns one chevron. */
@component({ name: "std-turn-tool" })
export class TurnTool extends LonaWebComponent {
  private $row: Accordion = this.hydrate("row");

  static makeWith(props: TurnTool.Props): TurnTool {
    return (TurnTool.make() as TurnTool).bind(props);
  }

  bind(props: TurnTool.Props): this {
    this.setAttribute("status", props.status);
    void this.$row.bind({
      items: [TurnTool.item(props)],
      expandedIndexes: TurnTool.discloses(props) && (props.expanded ?? false)
        ? [0]
        : [],
    });
    return this;
  }

  /** The row as an accordion ITEM, so a group can nest it rather than embed
   * a second component.
   *
   * A row is not a component that happens to contain an accordion — it is one
   * accordion item. Saying so is what lets `tool.group` hand its rows to the
   * accordion as `children`: one disclosure primitive owns every level, so the
   * chevron columns of a group and of the rows it opens are the same column by
   * construction rather than by two components agreeing on a number.
   *
   * A row with nothing to show passes an empty `content`, and the accordion
   * reads that as "nothing to disclose": it keeps the chevron COLUMN — which
   * is what makes a run of calls share one text edge — and drops the glyph,
   * the click and the focus stop. That used to be a rule here
   * (`:host([expandable])` suppressing the icon), which only worked while a
   * row was its own host. */
  static item(props: TurnTool.Props): Accordion.Item {
    return {
      header: TurnTool.buildHeader(props),
      content: TurnTool.discloses(props)
        ? TurnTool.buildDetail(props.detail ?? { text: "" })
        : "",
      open: TurnTool.discloses(props) && (props.expanded ?? false),
    };
  }

  /** Whether this call's output is worth a disclosure at all.
   *
   * Revised on review: "tool calls that are one line shouldn't be expandable."
   *
   * A chevron over one line is the same ceremony `tool.group` already refuses
   * for a run of one call — a control that costs a click, a hit target and a
   * column to reveal something that would have fitted on the row it is
   * hiding. So a one-line output is not a body; it is the row's last fact,
   * and `buildHeader` writes it into the sentence after the trailing note.
   *
   * The test is the LINE, not the length. A short multi-line output still
   * discloses, because its shape is what makes it worth reading apart —
   * a diff, a stack, a table — and a long one-line output still rides the
   * row, where the row's own ellipsis is already the answer to long. */
  private static discloses(props: TurnTool.Props): boolean {
    return (props.detail?.text ?? "").includes("\n");
  }

  /** What the row says about the body it is hiding.
   *
   * A one-line output rides the row whole — see `discloses`. A folded one
   * used to say NOTHING, and that is what made a long turn read as a wall:
   * 52 of 55 rows in one real session disclosed, so 52 rows rendered as
   * identically closed boxes with no way to tell a passing test from a stack
   * trace without opening each one. A chevron is a control, not information.
   *
   * So a folded row shows its first line and how many it is holding back,
   * which is the shape Claude Code ships (`⎿ 212 passed` / `+8 lines`) and
   * the one the prior-art page recommended and nobody built. Leading blank
   * lines are skipped because a command whose output starts with a newline
   * would otherwise preview as empty. */
  private static preview(props: TurnTool.Props): string {
    const text = (props.detail?.text ?? "").replace(/\s+$/, "");
    if (text === "") return "";
    if (!TurnTool.discloses(props)) return text;
    const lines = text.split("\n");
    const index = lines.findIndex((line) => line.trim() !== "");
    const first = index < 0 ? "" : lines[index];
    const hidden = lines.length - index - 1;
    return hidden > 0 ? `${first}   +${hidden} lines` : first;
  }

  /** The row's one line, built as an element because it goes INTO the
   * accordion's shadow root.
   *
   * Everything it needs travels on the elements themselves: a stylesheet
   * written in this component's root does not reach across that boundary,
   * and the first time this was tried with classes the result rendered in
   * the default face with none of its spacing. Typography's sheet IS in
   * every shadow root, so the ramp classes resolve — only the values are
   * inline. */
  private static buildHeader(props: TurnTool.Props): HTMLElement {
    const $header = document.createElement("div");
    Object.assign($header.style, {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: "0",
      flex: "1",
    });

    $header.append(TurnTool.buildMark(props.status));

    // One word for what happened. The dialect's verb wins; the tool's own
    // name stands in when it did not supply one.
    const label = props.verb ?? props.tool;
    if (label !== "") {
      const $label = Typography.small(label);
      Object.assign($label.style, {
        flex: "none",
        marginRight: "var(--tool-label-gap, 6px)",
        fontWeight: "500",
        color: "var(--text-sec)",
        whiteSpace: "nowrap",
      });
      $header.append($label);
    }

    // Mono, because a path or a command is scanned character by character,
    // and one line, because a session carries thousands.
    const $title = Typography.small(props.title);
    Object.assign($title.style, {
      minWidth: "0",
      fontFamily:
        "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)",
      color: "var(--text-ter)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    });
    $header.append($title);

    // Immediately after the subject, NOT pinned to the right margin: the
    // fact belongs to the subject, and a right-pinned column of facts is a
    // column of orphans. The separator is doing real work — both halves are
    // monospace and dim, so without it "deno check html/routes/*.ts exit 1"
    // reads as one argument list.
    const note = props.note ?? "";
    if (note !== "") {
      const $dot = Typography.small("·");
      Object.assign($dot.style, {
        flex: "none",
        color: "var(--text-ter)",
        opacity: "0.6",
      });
      const $note = Typography.small(note);
      Object.assign($note.style, {
        flex: "none",
        fontFamily:
          "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)",
        color: "var(--text-ter)",
        whiteSpace: "nowrap",
        opacity: "0.85",
      });
      $note.style.setProperty(
        "--font-size",
        "var(--turn-fine-size, var(--text-8px))",
      );
      $header.append($dot, $note);
    }

    // A one-line output is the row's last fact, not a body behind a control —
    // see `discloses`. Same register as the note it follows, and the same
    // separator, because it is the same kind of thing: something the call
    // returned, short enough to say here. It shrinks before the subject does
    // (`flex: 0 1 auto` against the subject's `1`), so a long one ellipsises
    // rather than pushing the command out of the row.
    const inline = TurnTool.preview(props);
    if (inline !== "") {
      const $dot = Typography.small("·");
      Object.assign($dot.style, {
        flex: "none",
        color: "var(--text-ter)",
        opacity: "0.6",
      });
      const $inline = Typography.small(inline);
      Object.assign($inline.style, {
        flex: "0 1 auto",
        minWidth: "0",
        fontFamily:
          "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)",
        color: "var(--text-ter)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        opacity: "0.85",
      });
      $inline.style.setProperty(
        "--font-size",
        "var(--turn-fine-size, var(--text-8px))",
      );
      $header.append($dot, $inline);
    }
    return $header;
  }

  /** A DRAWN circle, not a glyph. Revised on review: "let's increase the
   * icon size and just use circles to indicate their status".
   *
   * Five text glyphs are five advance widths and five optical weights at
   * one nominal size, so the column was neither aligned nor evenly
   * weighted. One circle, varying only in fill and colour, is the same
   * information with none of that. The status is the FILL — hollow for work
   * not done, solid for work that finished — and colour says which. */
  private static buildMark(status: TurnTool.Status): HTMLElement {
    const $mark = document.createElement("div");
    $mark.setAttribute("aria-label", TurnTool.LABEL[status]);
    const tone = TurnTool.TONE[status];
    Object.assign($mark.style, {
      flex: "none",
      boxSizing: "border-box",
      width: "var(--tool-mark-size, 10px)",
      height: "var(--tool-mark-size, 10px)",
      borderRadius: "50%",
      border: `1.5px solid ${tone}`,
      background: status === "pending" || status === "declined"
        ? "transparent"
        : tone,
    });
    return $mark;
  }

  /** The call's output, verbatim, in the row's own word column.
   *
   * Revised on review: "remove the divider between tool calls. code-block has
   * too much padding-left and is not aligned."
   *
   * It used to hang on a rail at the chevron's centre with the text out at
   * the row's words — which put a 28px empty channel between the line and
   * the text it belonged to, and read as a divider dropped between one call
   * and the next rather than as one call's output. Both complaints are the
   * same object: a vertical rule needs something on both sides of it to be
   * worth its ink, and there was nothing on the left.
   *
   * So the rule is gone and the indent does the work alone. The text starts
   * exactly where the row's own words start — chevron, gap, mark, gap — so
   * the output reads as a continuation of the sentence above it, which is
   * what it is. Nothing here is a picked number: change the chevron or the
   * mark and this follows.
   *
   * The geometry is on the element, not in --accordion-content-padding,
   * because an ELEMENT handed to the accordion as content BECOMES the
   * padded box: `renderContent` puts `.accordion-content-custom` on the
   * element itself rather than wrapping it, so the knob and this panel's
   * own inline padding are the same declaration and inline wins. */
  private static buildDetail(detail: TurnTool.Props["detail"]): HTMLElement {
    const textX = "calc(var(--turn-chevron-width, 14px) + 6px +" +
      " var(--tool-mark-size, 10px) + 6px)";
    const $detail = document.createElement("pre");
    $detail.className = "code-block";
    const text = detail?.text ?? "";
    if (detail?.language === "diff") {
      $detail.append(...TurnTool.diffLines(text));
    } else {
      $detail.textContent = text;
    }
    Object.assign($detail.style, {
      margin: `2px 0 4px ${textX}`,
      padding: "0",
      color: "var(--text-sec)",
      whiteSpace: "pre",
      overflowX: "auto",
      maxHeight: "260px",
      overflowY: "auto",
    });
    $detail.style.setProperty(
      "--font-size",
      "var(--turn-fine-size, var(--text-8px))",
    );
    $detail.style.setProperty("--line-height-ratio", "1.55");
    return $detail;
  }

  /** A unified diff's meaning is in the first character of each line, so a
   * diff rendered as undifferentiated monospace has thrown away the only
   * thing that distinguishes it from any other block of text.
   *
   * Colour is the whole treatment — no background bands, no gutter. A patch
   * sits inside a disclosure under one row of a transcript, and a row that
   * turns into a two-tone block when opened reads as a different surface
   * rather than as this row's body. `+++`/`---` are file headers, not
   * additions and removals, which is why they are excluded before the
   * single-character test. */
  private static diffLines(text: string): Node[] {
    // `TurnDiff.classify` is the shared semantic pass (hunk-aware, so
    // `--- a/x` inside a hunk is a removal and `-1 file changed` in a
    // summary tail is chrome); this skin's whole treatment is colour on the
    // RAW line, signs kept.
    return TurnDiff.classify(text.split("\n")).map((row, index) => {
      const $line = document.createElement("span");
      $line.textContent = index === 0 ? row.raw : `\n${row.raw}`;
      $line.style.color = TurnTool.DIFF_TONE[row.kind];
      return $line;
    });
  }

  private static readonly DIFF_TONE: Record<TurnDiff.Kind, string> = {
    add: "var(--green, #51b748)",
    del: "var(--red, #fe887c)",
    ctx: "var(--text-sec)",
    hunk: "var(--text-ter)",
    meta: "var(--text-ter)",
  };

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
        /* The row's vertical rhythm, and it is PADDING rather than a gap
           between rows. Revised on review: "need more gap between rows."

           Padding, because the same space has two jobs: it separates one
           call from the next AND it is the room the hover highlight fills.
           A margin would have bought the first and left the highlight the
           same cramped pill it was, hugging the text with nowhere to go.

           Measured: the row's ink — a 10px mark against 12.5px words — is
           14px tall, so 3px a side puts the pitch at 20px and the highlight
           at 18px of that. */
        --turn-row-pad-y: 3px;
        /* How far the highlight reaches PAST the row horizontally, as one
           length rather than a written-out inset: the group derives its
           subtree's right padding from the same number, so the outermost
           row's highlight has somewhere to go instead of being clipped by
           the region that animates the group open. Two places, one number. */
        --turn-row-bleed-x: 6px;
        /* Vertically it goes the other way — 1px IN, so two consecutive
           highlights read as two rows rather than merging into a bar. */
        --turn-row-hover-inset-y: 1px;
      }
      /* Every value the row needs, and nothing drawn by hand. Revised on
         review: "we're struggling with this layout and there are constant
         bugs. this is essentially a smaller accordian. can we reuse the
         accordian and just tweak its values with css variables."

         The accordion's own defaults are a page section's — 12px of header
         padding, an icon column sized for a heading, 36px of content
         indent. A transcript row is a denser object, and that difference is
         the only thing this block says. */
      /* The family's FINE register: trailing facts, counts, verbatim
         output - the runs that qualify the words beside them and must read
         quieter than those words.

         NOT --caption-size. The app names that register, but this theme
         resolves it equal to --small-size, so the fallback chain
         var(--caption-size, var(--small-size)) never produced a second size
         and every fine run rendered at the row size. Measured across the
         family: 307 of 380 text runs at exactly 12.5px, 127 of them mono
         facts sized identically to the sentences they annotate.

         --text-8px is the one ramp step below the row size. It renders at
         11.11px, not 8: --font-size is a CAP-HEIGHT box, so a token names
         the box and the ramp divides by (1 - cap - baseline) = 0.72. One
         name, one default, and a caller retunes the whole family by setting
         --turn-fine-size once on the transcript. A theme that grows a real
         --caption-size points this at it in one place.
      */
      #row {
        --accordion-header-padding: var(--turn-row-pad-y) 0;
        --accordion-header-gap: 6px;
        --accordion-icon-size: var(--turn-chevron-width, 14px);
        --accordion-icon-svg-size: var(--turn-chevron-size, 10px);
        /* Zero, because the output panel IS the padded box — see the note
           on buildDetail — and carries its own rail geometry. */
        --accordion-content-padding: 0px;
        /* Revised on review: "we need to grow the hover state so it extends
           past its bounds."

           The accordion's 4px/8px default is an inset because a page
           section's header is 12px-padded — the highlight lives inside that
           padding. A transcript row had none, so the same inset drew a pill
           SHORTER and NARROWER than the line it was lighting, clipping the
           status mark at one end and the trailing fact at the other.

           It goes outward horizontally, past the text on both sides. It does
           NOT go outward vertically: that height is the row's padding now,
           which is a better place for it — the space between two calls and
           the height of the highlight are the same fact, and writing it once
           means they cannot disagree. */
        --accordion-header-hover-inset: var(--turn-row-hover-inset-y)
          calc(-1 * var(--turn-row-bleed-x));
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-accordion id="row"></std-accordion>
  `;
}

/** `tool.group` — a run of calls folded into one line.
 *
 * A turn that reads nine files before answering spends nine rows saying so,
 * and a reader skimming for the answer pays for all of them. Both vendor
 * surfaces solved this the same way: ChatGPT collapses a research run into
 * "Searched the web · 12 sources", Claude folds a burst of tool use behind
 * one summary line. The calls are still there; they are just not the first
 * thing you read.
 *
 * The rule that keeps this honest is the failure rule: **a group never
 * hides a failure.** A collapsed summary that quietly contains a failed
 * call is worse than nine rows, because the reader now believes the turn
 * went fine. So a group holding any failed or declined call opens itself,
 * and its summary names the count in the status's own colour either way.
 *
 * The group builds its rows from `TurnTool.Props`, so it cannot drift from
 * the row it summarizes, and one call is rendered as a bare row — a
 * disclosure that hides a single line is pure ceremony.
 *
 * ## It is an accordion, because it was already one
 *
 * Revised on review: "the 'used tool' is misaligned. this is essentially a
 * nested accordion."
 *
 * That is the whole diagnosis. This was a hand-rolled disclosure — a button,
 * a chevron, a summary, a hidden div — sitting one import away from
 * `std-accordion`, which is that object and has been all along. Every
 * alignment complaint filed against it was a symptom of re-deriving a
 * layout the app already had: the summary sat on a baseline row whose
 * columns were maintained by hand against the rows underneath, and hand-kept
 * columns drift.
 *
 * Binding the accordion removes the question rather than answering it. The
 * header's chevron, its hit target, its hover, its focus ring and the
 * alignment between them are the accordion's, and the rows it opens are its
 * `children` — the same primitive one level down, not a second one embedded.
 * What is left here is the only thing this component ever actually owned: the
 * summary's WORDING, the failure rule, and the density a transcript wants. */
@component({ name: "std-turn-tool-group" })
export class TurnToolGroup extends LonaWebComponent {
  private $group: Accordion = this.hydrate("group");
  private $single: HTMLElement = this.$("single");

  static makeWith(props: TurnToolGroup.Props): TurnToolGroup {
    return (TurnToolGroup.make() as TurnToolGroup).bind(props);
  }

  bind(props: TurnToolGroup.Props): this {
    const failed = props.tools.filter((tool) =>
      tool.status === "failed" || tool.status === "declined"
    ).length;

    // One call needs no disclosure: the summary line would be taller than
    // the thing it summarizes. It bypasses the accordion entirely rather
    // than being an accordion with its header hidden — a header that is
    // never shown is still a header keeping a column.
    const single = props.tools.length === 1;
    this.toggleAttribute("single", single);
    if (single) {
      this.$single.replaceChildren(TurnTool.makeWith(props.tools[0]));
      void this.$group.bind({ items: [], expandedIndexes: [] });
      return this;
    }
    this.$single.replaceChildren();

    // The rows are the accordion's CHILDREN, not a block of components handed
    // to it as opaque content. Revised on review: "the tooling should be a
    // nested accordian."
    //
    // Content would have worked and did, and it is the wrong shape: a stack of
    // std-turn-tool elements is a second disclosure implementation living
    // inside the first, each with its own root, its own chevron column and its
    // own animation, and the two levels line up only for as long as both keep
    // agreeing about a number. As children they are one accordion — one
    // height-animation model (a row opening flows into the group's own
    // `height: auto` with no second animation), one hover language, and the
    // chevron columns are the same column by construction.
    void this.$group.bind({
      items: [{
        header: this.buildHeader(props, failed),
        children: props.tools.map((tool) => TurnTool.item(tool)),
      }],
      // A failure is never folded away. An explicit `expanded` still wins —
      // a caller replaying a decided transcript may want everything shut.
      expandedIndexes: (props.expanded ?? failed > 0) ? [0] : [],
    });
    return this;
  }

  /** The summary line, handed to the accordion as an element.
   *
   * As an element rather than a string because the failure count is a
   * second register inside one header — the accordion's own header text is
   * a single styled block, and a string could not carry the red.
   *
   * Styled INLINE, which is not a shortcut. This element is handed across a
   * shadow boundary: the accordion appends it inside its own root, where a
   * stylesheet declared in this component's root cannot reach it. That is
   * not a quirk to work around, it is the boundary doing its job, and the
   * accordion's own note on slotted headers says the same thing. The first
   * attempt used classes and rendered "Used 3 tools2 failed" in the default
   * face — no gap, no separator, no red — because every rule silently
   * missed. Anything that must survive the crossing travels on the element. */
  private buildHeader(
    props: TurnToolGroup.Props,
    failed: number,
  ): HTMLElement {
    const $header = document.createElement("div");
    Object.assign($header.style, {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: "0",
    });

    // Ramp elements, not spans with a literal size: <p class="small"> is
    // what every dense row in this repo is built from, and Typography's
    // sheet is in the accordion's shadow root too, so the class resolves
    // there. Only --font-size travels inline.
    const $summary = Typography.small(
      props.summary ?? TurnToolGroup.describe(props.tools),
    );
    Object.assign($summary.style, {
      // A LITERAL zero, not --margin-top. Typography trims a text block with
      // negative margins computed directly from the cap height, so the
      // variable is never read and setting it does nothing — measured, a
      // .small kept -2.19px/-2.56px with it zeroed. Flex lays out MARGIN
      // boxes, so an untrimmed run makes this header participate as 9px
      // against 13.75px of ink, and the row sizes itself to something else.
      marginTop: "0",
      marginBottom: "0",
      fontWeight: "500",
      color: "var(--text-sec)",
      whiteSpace: "nowrap",
    });
    $header.append($summary);

    // The one thing a collapsed group is not allowed to whisper — and it
    // says it in COLOUR ONLY. Revised on review: "'1 failed' typography after
    // the 'used 4 tools' is too big. keep it [in the summary's face], make
    // the font sizes the same."
    //
    // It was mono at --caption-size against a summary set in the text face at
    // --small-size. Those two aliases resolve equal in this theme, so the
    // "too big" was the FACE: a monospace glyph carries more ink at the same
    // nominal size, and it read as a louder register beside the words it
    // qualifies. Mono earns its place on a path or a command, where column
    // alignment is the content — "1 failed" is prose, and it is the same
    // sentence as the summary. So it takes the summary's face and the
    // summary's size, and differs in the one dimension that carries the
    // meaning.
    if (failed > 0) {
      const $dot = Typography.small("·");
      Object.assign($dot.style, {
        marginTop: "0",
        marginBottom: "0",
        color: "var(--text-ter)",
        opacity: "0.6",
      });
      const $failed = Typography.small(`${failed} failed`);
      Object.assign($failed.style, {
        flex: "none",
        marginTop: "0",
        marginBottom: "0",
        color: "var(--fg-red, #e5484d)",
      });
      $header.append($dot, $failed);
    }
    return $header;
  }

  static $styles = [
    css`
      :host {
        display: block;
        min-width: 0;
        /* The same three as the row's — see std-turn-tool for what each one
           buys. The rows inside a group are this accordion's own items now,
           so their padding and hover are declared here rather than there,
           and the subtree's right padding comes off the same x. */
        --turn-row-pad-y: 3px;
        --turn-row-bleed-x: 6px;
        --turn-row-hover-inset-y: 1px;
      }
      :host([single]) #group {
        display: none;
      }
      /* Dense, and flush. The accordion is built for sections of a page, so
         its defaults are a page's: 12px of header padding, a divider under
         every item, an icon column sized for a heading. A transcript row is
         none of those things, and this is the one thing this component
         still owns.

         The header's inline padding goes to ZERO so the accordion's chevron
         column lands on the same x as the chevron of every row it opens.
         That column is what the review was reading as misaligned, and it is
         now the same column by construction rather than by two components
         agreeing on a number. */
      #group {
        --accordion-header-padding: var(--turn-group-pad, var(--turn-row-pad-y))
          0;
        --accordion-header-gap: 6px;
        --accordion-icon-size: var(--turn-chevron-width, 14px);
        --accordion-icon-svg-size: var(--turn-chevron-size, 10px);
        --accordion-content-padding: 0px;
        --accordion-header-hover-inset: var(--turn-row-hover-inset-y)
          calc(-1 * var(--turn-row-bleed-x));
        /* The subtree's geometry, derived from the chevron column rather than
           picked. The rail hangs on the chevron's own centre line — half the
           column, less half the 2px rail — so the line under a group is the
           line the group's disclosure sits on. The same figure again as the
           indent puts the rail dead centre of a one-chevron gutter and starts
           the child column exactly one column in, which is the whole visual
           claim of nesting: these rows belong to that line.

           The right padding is not decoration. Rows bleed their hover 6px past
           their own box (see #row) and .accordion-content clips to animate its
           height, so without the matching room the hover would be sliced down
           its right edge. */
        --turn-nest-gutter: calc(var(--turn-chevron-width, 14px) / 2 - 1px);
        --accordion-nest-padding: 0 var(--turn-row-bleed-x) 4px
          var(--turn-nest-gutter);
        --accordion-nest-indent: var(--turn-nest-gutter);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-accordion id="group"></std-accordion>
      <div id="single"></div>
    </div>
  `;
}

export namespace TurnTool {
  export type Status = "pending" | "running" | "done" | "failed" | "declined";

  /** The circle carries the status visually; this is what it is called
   * for a reader who cannot see it. */
  export const LABEL: Record<Status, string> = {
    pending: "pending",
    running: "running",
    done: "done",
    failed: "failed",
    declined: "declined",
  };

  /** The circle's colour, one per status.
   *
   * Through --fg-*, NOT the raw palette hues: the theme's --blue, --green
   * and --red are SYSTEM colours picked to read on a dark surface, and the
   * --fg-* twins are each mixed 72/28 toward --text-pri so they stay
   * legible on either.
   *
   * `declined` writes that mix out by hand because --fg-yellow does not
   * exist. Measured on the dark theme: --fg-blue, --fg-red and --fg-green
   * resolve; --fg-purple, --fg-yellow and --fg-teal do not. An undefined
   * custom property does not fall back to a related token, it falls to the
   * literal here — so `var(--fg-yellow, #d4a72c)` would have shipped a dark
   * amber on a dark panel, which is the bug std-exception's diagnostic code
   * had. Computing the recipe lands where the token would have. */
  export const TONE: Record<Status, string> = {
    pending: "var(--text-ter)",
    running: "var(--fg-blue, #3b82f6)",
    done: "var(--fg-green, #2da44e)",
    failed: "var(--fg-red, #e5484d)",
    declined:
      "var(--fg-yellow, color-mix(in srgb, var(--yellow, #d4a72c) 72%," +
      " var(--text-pri, #1f2328) 28%))",
  };

  export type Props = {
    /** The tool's own name — "read", "execute", "web_search". Used as the
     * row's one word when the dialect supplied no `verb`; "" for a surface
     * that runs a single tool and says so once, not per row. */
    tool: string;
    /** What the tool DID, in the dialect's own words — "Searched", "Ran",
     * "Generated". Open text: the translator owns the wording, so a vendor
     * that invents a tenth call type needs no change here. Wins over
     * `tool`; the row shows one word, never both. */
    verb?: string;
    /** The subject: a path, a query, a command, a prompt. */
    title: string;
    /** A trailing fact the row earned — "6 sources", "161 lines", "1024x1024". */
    note?: string;
    /** The call's output, verbatim, with an optional hint about what the text
     * IS. Closed by default; its presence is what makes the row expandable at
     * all. The hint is about the text and never about its box — how tall the
     * body gets stays this component's decision. */
    detail?: {
      text: string;
      language?: "plain" | "lisp" | "js" | "ts" | "rust" | "diff";
    };
    expanded?: boolean;
    status: Status;
  };
}

export namespace TurnToolGroup {
  export type Props = {
    tools: TurnTool.Props[];
    /** Overrides the derived summary — a dialect that knows the run was a
     * web search should say so. */
    summary?: string;
    /** Forces the disclosure. Defaults to open when the run contains a
     * failed or declined call, closed otherwise. */
    expanded?: boolean;
  };

  /** The collapsed line's words when the caller supplies none.
   *
   * This deliberately does NOT list the tools. Listing them was the first
   * attempt and it produced "Used read, Ran, write": the row's label is a
   * tool NOUN for some calls and a dialect VERB for others, and the two do
   * not form one grammatical list. Since neither vendor surface names them
   * either — the point of folding is that the reader does not care yet —
   * the count is the honest summary, and a dialect that knows the run was
   * one thing ("Searched the web") passes `summary` and says so. */
  export function describe(tools: TurnTool.Props[]): string {
    return `Used ${tools.length} tools`;
  }
}
